/**
 * 메뉴 아이콘 선택 목록.
 *
 * 코드 사이드바가 실제로 쓰는 아이콘(menuCodeDefaults 의 CODE_ICON_NAMES)을 전부 담고,
 * 새 메뉴용으로 흔히 쓰는 것들을 더했다. 임의의 lucide 이름을 그대로 받지 않는 이유는
 * (1) 번들에 아이콘 전체가 딸려 오고 (2) 오타 하나로 메뉴가 빈칸이 되기 때문이다.
 * 목록 밖 이름은 API 가 거부하고, 혹시 들어와도 렌더 시 null 로 떨어져 코드 아이콘을 쓴다.
 */
import React from "react";
import {
  BarChart3,
  Bell,
  BookOpen,
  Bookmark,
  Box,
  Boxes,
  Braces,
  Briefcase,
  Building,
  Building2,
  Calculator,
  CalendarDays,
  CalendarRange,
  ChartPie,
  ClipboardList,
  Clock,
  Cog,
  Database,
  Dot,
  FileAudio,
  FileSignature,
  FileText,
  Files,
  Flag,
  FlaskConical,
  FolderKanban,
  FolderOpen,
  FolderTree,
  GanttChartSquare,
  Gift,
  GitBranch,
  Globe,
  Handshake,
  HardDrive,
  Headset,
  Heart,
  Home,
  Image,
  Inbox,
  KanbanSquare,
  Key,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  LayoutTemplate,
  Link2,
  ListChecks,
  Lock,
  Mail,
  Map,
  Megaphone,
  MessageSquare,
  Monitor,
  Package,
  PenLine,
  Phone,
  PieChart,
  Printer,
  ReceiptText,
  Rocket,
  Search,
  Send,
  Server,
  Settings,
  Share2,
  Shield,
  ShieldCheck,
  ShoppingCart,
  Sliders,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  Star,
  Store,
  Table,
  Tag,
  Target,
  Trash2,
  TrendingUp,
  Truck,
  Upload,
  UserRound,
  Users,
  Video,
  Wallet,
  Warehouse,
  Wrench,
  Zap,
} from "lucide-react";

/** lucide 컴포넌트 타입을 그대로 받는다 — 좁은 시그니처로 고정하면 propTypes 가 안 맞는다. */
type IconComponent = (props: { size?: number }) => JSX.Element;

export const MENU_ICONS = {
  BarChart3,
  Bell,
  BookOpen,
  Bookmark,
  Box,
  Boxes,
  Braces,
  Briefcase,
  Building,
  Building2,
  Calculator,
  CalendarDays,
  CalendarRange,
  ChartPie,
  ClipboardList,
  Clock,
  Cog,
  Database,
  Dot,
  FileAudio,
  FileSignature,
  FileText,
  Files,
  Flag,
  FlaskConical,
  FolderKanban,
  FolderOpen,
  FolderTree,
  GanttChartSquare,
  Gift,
  GitBranch,
  Globe,
  Handshake,
  HardDrive,
  Headset,
  Heart,
  Home,
  Image,
  Inbox,
  KanbanSquare,
  Key,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  LayoutTemplate,
  Link2,
  ListChecks,
  Lock,
  Mail,
  Map,
  Megaphone,
  MessageSquare,
  Monitor,
  Package,
  PenLine,
  Phone,
  PieChart,
  Printer,
  ReceiptText,
  Rocket,
  Search,
  Send,
  Server,
  Settings,
  Share2,
  Shield,
  ShieldCheck,
  ShoppingCart,
  Sliders,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  Star,
  Store,
  Table,
  Tag,
  Target,
  Trash2,
  TrendingUp,
  Truck,
  Upload,
  UserRound,
  Users,
  Video,
  Wallet,
  Warehouse,
  Wrench,
  Zap,
} as unknown as Record<string, IconComponent>;

/** 선택 UI에 쓸 이름 목록 (알파벳 순) */
export const MENU_ICON_NAMES = Object.keys(MENU_ICONS).sort();

/** 직접 추가한 메뉴가 아이콘을 안 고른 경우의 기본값 */
export const DEFAULT_CUSTOM_ICON = "Dot";

/** 이름 → 아이콘 요소. 목록에 없으면 null(호출부가 코드 아이콘을 유지한다). */
export function renderMenuIcon(name: string | null | undefined, size = 16): React.ReactNode {
  if (!name) return null;
  const Icon = MENU_ICONS[name];
  return Icon ? <Icon size={size} /> : null;
}
